import React from 'react';
import { Input } from '@/components/ui/input';
import { Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useLingui } from '@lingui/react/macro';

interface TableSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

/**
 * Search input component for tables with clear functionality.
 * Reports every keystroke; BaseEditableTable debounces before it filters or
 * calls `onSearchChange`.
 */
export const TableSearch = React.forwardRef<HTMLInputElement, TableSearchProps>(
  ({ value, onChange, placeholder, className }, ref) => {
    const { t } = useLingui();
    
    const handleClear = () => {
      onChange('');
    };

    return (
      <div className={cn('relative', className)}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={ref}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder || t`table.search.defaultPlaceholder`}
            className="pl-9 pr-9 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-input"
            aria-label={t`table.search.ariaLabel`}
          />
          {value && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0 hover:bg-muted"
              aria-label={t`table.search.clearAriaLabel`}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    );
  }
);

TableSearch.displayName = 'TableSearch';